import { Box, Link, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React from 'react'

import { useSearchFilters } from '../../../providers/search'
import { formatLinkWithTicker } from '../../../utils/functions'
import Html from '../../Html/Html'
import { dateRenderer } from '../../Tables/ScrollingTable'
import WhiteScrollingTable from '../../Tables/WhiteScrollingTable'

// Styled Components
const HtmlLink = styled('div')(({ theme }) => ({
  '& a': {
    textDecoration: 'none',
    color: 'rgb(9, 42, 72)',
  },
}))

const TextLink = styled('button')(({ theme }) => ({
  background: 'none',
  color: 'rgb(9, 42, 72)',
  textDecoration: 'underline',
  border: 'none',
  padding: 0,
  font: 'inherit',
  cursor: 'pointer',
  outline: 'inherit',
}))

const TableContent = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
    marginLeft: -16,
    marginRight: -16,
  },
  marginTop: theme.spacing(4),
}))

const handleClearFilter = () => {
  if (typeof window !== 'undefined') {
    window.location.reload()
  } else {
    console.warn('window is undefined. Cannot reload the page.')
  }
}

const linkStyles = { fontWeight: '500', color: 'rgb(9, 42, 72)' }

const AllFundRecentDistributions = ({
  data = [],
  fundGrouping,
  activeShareClass,
  html_content_before = [],
}) => {
  const { filters, applyFilter } = useSearchFilters()

  const distributionRenderer = (props) => (
    <span>
      {typeof props.value === 'number' ? props.value.toFixed(5) : '--'}
    </span>
  )

  const priceRenderer = (props) => (
    <span>
      {typeof props.value === 'number' ? props.value.toFixed(2) : '--'}
    </span>
  )

  const fundNameRenderer = (props) => (
    <Link
      href={formatLinkWithTicker(props.row)}
      underline="none"
      sx={linkStyles}
    >
      {props.value}
    </Link>
  )

  const columns = [
    {
      Header: 'Fund Name',
      accessor: 'title',
      id: 'title',
      Cell: fundNameRenderer,
      sortable: true,
    },
    {
      Header: 'Ticker',
      accessor: 'ticker',
      id: 'ticker',
      Cell: fundNameRenderer,
      sortable: true,
    },
    {
      Header: 'Inception Date',
      accessor: 'inception_date',
      id: 'inception_date',
      Cell: dateRenderer,
      sortable: true,
    },
    {
      Header: 'Record Date',
      accessor: (a) => a.recordDateCalendar?.date || '',
      id: 'recordDateCalendar',
      Cell: dateRenderer,
      sortable: true,
    },
    {
      Header: 'Reinvest / Payable Date',
      accessor: (a) => a.payableDateCalendar?.date || '',
      id: 'payableDateCalendar',
      Cell: dateRenderer,
      sortable: true,
    },
    {
      Header: 'Ordinary Income',
      accessor: 'ordinaryIncome',
      id: 'ordinaryIncome',
      Cell: distributionRenderer,
      sortable: true,
    },
    {
      Header: 'Short Term Capital Gains',
      accessor: 'shortTermCapitalGains',
      id: 'shortTermCapitalGains',
      Cell: distributionRenderer,
      sortable: true,
    },
    {
      Header: 'Long Term Capital Gains',
      accessor: 'longTermCapitalGains',
      id: 'longTermCapitalGains',
      Cell: distributionRenderer,
      sortable: true,
    },
    {
      Header: 'Total Distribution',
      accessor: 'totalDividend',
      id: 'totalDividend',
      Cell: distributionRenderer,
      sortable: true,
    },
    {
      Header: 'Foreign Tax Credit',
      accessor: 'foreignTaxCredit',
      id: 'foreignTaxCredit',
      Cell: distributionRenderer,
      sortable: true,
    },
    {
      Header: 'Reinvest Price',
      accessor: 'reinvestmentPrice',
      id: 'reinvestmentPrice',
      Cell: priceRenderer,
      sortable: true,
    },
  ]

  const fundData = data?.reduce((acc, fund) => {
    if (fund?.latestDistribution) {
      acc[fund.ticker] = { ...fund, ...fund.latestDistribution }
    }
    return acc
  }, {})

  const findFundByShareClass = (fund) => {
    return fund.fund_classes?.find(
      (shareClass) => shareClass.share_class[0]?.id === activeShareClass.id
    )
  }

  const tables = fundGrouping?.group?.map((fundGroup) => {
    const tableData = fundGroup.products
      .map((fund) => {
        const fundByShareClass = findFundByShareClass(fund)
        if (fundByShareClass) {
          return {
            ...fundData[fundByShareClass.ticker],
            ...fundByShareClass,
            ...fund,
          }
        }
        return null
      })
      .filter(Boolean)

    return {
      title: fundGroup.title,
      data: [
        { title: fundGroup.title, innerTitle: fundGroup.title },
        ...tableData,
      ],
      columns,
    }
  })

  const filteredTables = tables
    ?.map((table) => applyFilter?.(table, filters))
    .filter(Boolean)

  const combinedTable = filteredTables?.reduce(
    (acc, table) => {
      acc.data.push(...table.data)
      return acc
    },
    { data: [] }
  )

  // Handler for clearing filters with SSR safety
  let firstColumnWidth = `${100 / columns.length}%`
  return (
    <>
      {html_content_before?.map((content_block, idx) => (
        <Html key={idx} content={content_block.html} className={HtmlLink} />
      ))}
      {combinedTable?.data?.length === 0 ? (
        <Typography variant="body2">
          Your criteria produced no results.{' '}
          <TextLink onClick={handleClearFilter} aria-label="Clear Filters">
            Clear filter
          </TextLink>{' '}
          to explore our products.
        </Typography>
      ) : (
        filteredTables?.map((table, index) => (
          <TableContent key={index}>
            <WhiteScrollingTable
              maxFirstColumnWidth={firstColumnWidth}
              columns={columns}
              data={table.data}
              headerColPadding="16px 5px"
            />
          </TableContent>
        ))
      )}
    </>
  )
}

AllFundRecentDistributions.propTypes = {
  data: PropTypes.array,
  fundGrouping: PropTypes.shape({
    group: PropTypes.array.isRequired,
  }).isRequired,
  activeShareClass: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  html_content_before: PropTypes.array,
}

export default AllFundRecentDistributions